// Programas anuales (lote C, M48): tipos, etiquetas y constantes del motor común usado por cinco programas:
//  - capacitacion: TH 8, programa de formación, capacitación y entrenamiento.
//  - examenes:     PO 2, cronograma de planificación y ejecución de exámenes médicos ocupacionales.
//  - psicosocial:  PO 24 (implementado, 12 actividades) y PO 25 (registrado en el SUT).
//  - sustancias:   PO 26 (implementado, las 14 actividades de la Matriz MSP-MDT 2020) y PO 27 (registrado en el SUT).
//  - inspecciones: PO 14, programa anual de inspecciones internas (cronograma desde las programaciones de Inspecciones v2).
// Plazos DECIDIDOS POR CLAUDE, por validar con Kevin (se muestran al cliente como "sugeridos por StorSafe, no
// normativos"): aprobar el programa del año hasta el 31 de enero, 30 días de tolerancia para ejecutar una actividad
// vencida, periodicidad de exámenes de 12 meses y 60 días de anticipación para contar un examen hecho antes de su fecha.

import type { EstadoItem } from '../plan-emergencias-compliance-sync';

export type TipoPrograma = 'capacitacion' | 'examenes' | 'psicosocial' | 'sustancias' | 'inspecciones';
export const TIPOS_PROGRAMA: TipoPrograma[] = ['capacitacion', 'examenes', 'psicosocial', 'sustancias', 'inspecciones'];
export type EstadoVersion = 'borrador' | 'en_firma' | 'aprobada' | 'reemplazada';
export type MetodoFirma = 'pantalla' | 'papel';
export type RolFirmaPrograma = 'responsable_sso' | 'representante_legal';
export const ROLES_FIRMA: RolFirmaPrograma[] = ['responsable_sso', 'representante_legal'];
export type Destinatarios = 'todos' | 'puesto' | 'trabajador';
export type TipoExamen = 'pre_ocupacional' | 'periodico' | 'reintegro' | 'retiro';
export const TIPOS_EXAMEN: TipoExamen[] = ['pre_ocupacional', 'periodico', 'reintegro', 'retiro'];

export const BUCKET_PROGRAMAS = 'annual-program-files';
export const SEGUNDOS_URL_PROGRAMAS = 120;
export const ARCHIVO_MAX_MB = 10;
export const ARCHIVO_MAX_BYTES = ARCHIVO_MAX_MB * 1024 * 1024;

// ── Plazos sugeridos (no normativos) ──
export const MES_LIMITE_APROBACION = 1;
export const DIA_LIMITE_APROBACION = 31;
export const DIAS_TOLERANCIA_EJECUCION = 30;
export const PERIODICIDAD_EXAMENES_DEFECTO = 12;
export const DIAS_ANTICIPACION_EXAMEN = 60;
export const NORMA_PLAZOS = 'Plazos sugeridos por StorSafe, no normativos';
export const NOTA_PLAZOS = `${NORMA_PLAZOS}: aprobar el programa del año hasta el ${DIA_LIMITE_APROBACION} de enero y ejecutar cada `
  + `actividad dentro de los ${DIAS_TOLERANCIA_EJECUCION} días siguientes a su fecha planificada.`;
export const NOTA_PERIODICIDAD = `Periodicidad sugerida por StorSafe, no normativa: ${PERIODICIDAD_EXAMENES_DEFECTO} meses. `
  + 'La empresa la ajusta según la matriz de exámenes por puesto y el criterio del médico ocupacional.';
export const TEXTO_FIRMA_PROGRAMA = 'Firma electrónica simple: nombre, cédula, fecha y hora del servidor y huella SHA-256 del contenido aprobado.';

export const FUENTE_ANEXO1 = 'Anexo 1, Lista de verificación de cumplimiento de obligaciones de seguridad y salud en el trabajo (Ministerio del Trabajo)';
/** PO 26: de dónde salen las 14 actividades (en pantalla y en el PDF). */
export const CITA_MATRIZ_SUSTANCIAS = 'Matriz de actividades MSP-MDT (2020), Acuerdo Interministerial MDT-MSP-2019-0038';

export interface ConfigPrograma {
  titulo: string;
  corto: string;
  item: string;            // ítem del Checklist que calcula
  itemSut: string | null;  // ítem del registro en el SUT
  norma: string;
  contenidoMinimo: string; // lo que pide el Anexo 1 (se muestra como ayuda)
  numeroActividades: number | null;   // 12 (psicosocial) o 14 (sustancias)
}

export const CONFIG: Record<TipoPrograma, ConfigPrograma> = {
  capacitacion: {
    titulo: 'Programa de formación, capacitación y entrenamiento', corto: 'Programa de capacitación', item: 'TH-08', itemSut: null,
    norma: 'Decisión 584 Art. 11 y 23; Decreto Ejecutivo 255 Art. 15, 16 y 28; Acuerdo Ministerial 196 Art. 4',
    contenidoMinimo: 'Objetivos, diagnóstico de necesidades, contenido (temas según los riesgos identificados), cronograma por puesto de trabajo, '
      + 'metodología, duración y frecuencia, responsables, material utilizado y firmas de responsabilidad.',
    numeroActividades: null,
  },
  examenes: {
    titulo: 'Cronograma de exámenes médicos ocupacionales', corto: 'Cronograma de exámenes', item: 'PO-02', itemSut: null,
    norma: 'Decisión 584 Art. 14 y 22; Resolución 957 Art. 5; Decreto Ejecutivo 255 (2024) Art. 15',
    contenidoMinimo: 'Planificación de los exámenes de ingreso, periódicos, de reintegro y de retiro por trabajador y puesto, y su ejecución.',
    numeroActividades: null,
  },
  psicosocial: {
    titulo: 'Programa de prevención de riesgo psicosocial', corto: 'Programa psicosocial', item: 'PO-24', itemSut: 'PO-25',
    norma: 'Acuerdo Ministerial MDT-2025-102, Art. 6 (reformado por el Acuerdo Ministerial MDT-2025-186)',
    contenidoMinimo: 'Las 12 actividades del programa, con el respaldo de la implementación de cada una, y su registro en el SUT.',
    numeroActividades: 12,
  },
  sustancias: {
    titulo: 'Programa de prevención integral del uso de alcohol, tabaco y otras drogas', corto: 'Programa de alcohol, tabaco y drogas',
    item: 'PO-26', itemSut: 'PO-27',
    norma: 'Acuerdo Interministerial MDT-MSP-2019-0038 (Registro Oficial 114, 06/01/2020)',
    contenidoMinimo: 'Las 14 actividades del programa, con el respaldo de la implementación de cada una, y su registro en el SUT.',
    numeroActividades: 14,
  },
  inspecciones: {
    titulo: 'Programa anual de inspecciones internas', corto: 'Programa de inspecciones', item: 'PO-14', itemSut: null,
    norma: 'Código del Trabajo Art. 42; Resolución 957 Art. 1; Decisión 584 Art. 4; Decreto Ejecutivo 255 (2024) Art. 28',
    contenidoMinimo: 'Objetivos, alcance, cronograma, áreas a inspeccionar, lista de verificación y firmas de responsabilidad.',
    numeroActividades: null,
  },
};

export const ETIQUETA_ESTADO_VERSION: Record<EstadoVersion, string> = {
  borrador: 'Borrador', en_firma: 'En firma', aprobada: 'Aprobada', reemplazada: 'Reemplazada',
};
export const ETIQUETA_ROL: Record<RolFirmaPrograma, string> = {
  responsable_sso: 'Responsable de seguridad y salud en el trabajo', representante_legal: 'Representante legal',
};
export const ETIQUETA_DESTINATARIOS: Record<Destinatarios, string> = { todos: 'Todos los trabajadores', puesto: 'Un puesto', trabajador: 'Un trabajador' };
export const ETIQUETA_EXAMEN: Record<TipoExamen, string> = {
  pre_ocupacional: 'Ingreso', periodico: 'Periódico', reintegro: 'Reintegro', retiro: 'Retiro',
};
export const ETIQUETA_ESTADO_ITEM: Record<EstadoItem, string> = {
  cumple: 'Cumple', en_proceso: 'En proceso', no_cumple: 'No cumple', pendiente: 'Pendiente',
};

// ── Filas de la base (M48) ──
export interface AjustesPrograma { periodicidad_meses?: number }

export interface FilaPrograma { id: string; company_id: string; kind: TipoPrograma; program_year: number; created_at: string }

export interface FilaVersion {
  id: string; company_id: string; program_id: string; version: number; status: EstadoVersion;
  objectives: string | null; scope_diagnosis: string | null; methodology: string | null; responsibles: string | null; resources: string | null;
  settings: AjustesPrograma; snapshot: ContenidoPrograma | null; content_hash: string | null;
  sign_method: MetodoFirma | null; scan_path: string | null; scan_name: string | null; scan_mime: string | null; scan_size: number | null;
  approved_at: string | null; approved_by: string | null; change_reason: string | null; created_at: string; updated_at: string;
}

export interface FilaActividad {
  id: string; company_id: string; program_id: string; version_id: string; activity_key: string; position: number;
  template_no: number | null; title: string; audience_kind: Destinatarios; audience_label: string | null;
  position_id: string | null; worker_id: string | null; planned_date: string; responsible: string | null;
  duration_hours: number | null; training_id: string | null; exam_type: TipoExamen | null; notes: string | null;
  /** Indicador opcional (p. ej. PO 26: "trabajadores sensibilizados / total de trabajadores × 100"). */
  indicator: string | null;
  /** PO 14: programación de Inspecciones v2 de la que sale la actividad. */
  schedule_id: string | null;
}

/** Lo que se edita de una actividad (sin las columnas que pone la base). */
export type ActividadEditable = Pick<FilaActividad, 'title' | 'audience_kind' | 'audience_label' | 'position_id' | 'worker_id' | 'planned_date'
  | 'responsible' | 'duration_hours' | 'training_id' | 'exam_type' | 'notes' | 'template_no' | 'indicator' | 'schedule_id'>;

export interface FilaFirmaPrograma {
  id: string; company_id: string; version_id: string; role: RolFirmaPrograma; signer_name: string; signer_cedula: string;
  image_path: string; content_hash: string; signed_at: string;
}

export interface FilaEjecucion {
  id: string; company_id: string; program_id: string; activity_key: string; executed_date: string;
  evidence_path: string | null; evidence_name: string | null; evidence_mime: string | null; evidence_size: number | null;
  training_id: string | null; medical_exam_id: string | null; linked_label: string | null; notes: string | null; created_at: string;
}

export interface FilaSut {
  id: string; company_id: string; program_id: string; registration_code: string; registration_date: string;
  constancia_path: string; constancia_name: string; constancia_mime: string; constancia_size: number; created_at: string;
}

// ── Contenido congelado (lo que se firma) ──
export interface ActividadContenido {
  clave: string; orden: number; numero_lista: number | null; actividad: string; destinatarios: Destinatarios; destinatarios_texto: string;
  fecha_planificada: string; responsable: string; duracion_horas: number | null; capacitacion_id: string | null; tipo_examen: TipoExamen | null;
  notas: string; indicador: string; programacion_id: string | null;
}
export interface ContenidoPrograma {
  tipo: TipoPrograma; empresa: string; anio: number; version: number; version_id: string;
  objetivos: string; alcance_diagnostico: string; metodologia: string; responsables: string; recursos: string;
  ajustes: AjustesPrograma; actividades: ActividadContenido[];
}

// ── Orígenes mínimos ──
export interface CapacitacionMin { id: string; title: string; scheduled_date: string; status: string | null }
export interface ExamenMin { id: string; worker_id: string | null; exam_date: string; exam_type: string | null }
export interface TrabajadorMin {
  id: string; full_name: string; position: string | null; status: string | null; start_date: string | null; end_date: string | null;
  last_medical_exam: string | null;
}
/** PO 14: programación de Inspecciones v2 (insp_schedules) con el nombre de su plantilla y de su sede. */
export interface ProgramacionMin {
  id: string; template_id: string; site_id: string | null; area: string | null; frequency: 'unica' | 'semanal' | 'mensual' | 'trimestral' | 'semestral' | 'anual';
  start_date: string; end_date: string | null; active: boolean; responsible_role: string | null; responsible_worker_id: string | null;
}
/** PO 14: inspección de Inspecciones v2 (solo cuenta firmada). */
export interface InspeccionMin {
  id: string; schedule_id: string | null; template_name: string; status: string; scheduled_date: string | null; performed_date: string | null;
  signed_at: string | null;
}
